import api from 'services/api';
import { REJECT, nullable, num, object, str, type Received } from 'validation';

import { readResponse } from 'api/shared/readResponse';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';

/**
 * The request body for both `POST /python_libraries` (create) and `PUT /python_libraries/:id`
 * (update) — the FE sends the same shape to both. **Permissive on purpose**: the server is the
 * validation authority (name/filename/content required, `.py` suffix, folder existence), so this
 * type does not re-encode those rules. A too-strict client type would block valid sends when the
 * backend rules loosen.
 *
 * EVERY FIELD IS A REQUIRED KEY, and that is load-bearing rather than tidiness — on update the
 * backend does not treat an omitted field as "leave alone":
 *
 * - `name`, `filename`, `content` — omitting any of them is a 400.
 * - `description` — written unconditionally (`SET description = $3` over `description || null`),
 *   so omitting it CLEARS the stored description rather than keeping it.
 * - `folder_id` — the one field whose *presence* is inspected
 *   (`hasOwnProperty(req.body, 'folder_id')` → `SET folder_id = CASE WHEN $6 THEN $7 ELSE
 *   folder_id END`). Present-and-null clears the folder; absent keeps it. An optional
 *   `folder_id?` here would let a caller silently keep the old folder while believing it had
 *   moved the library to the root.
 *
 * So a caller that forgets a field gets a compile error, not a silent data loss. `description` is
 * `string | null` because the column is nullable; sending `null` is equivalent to omitting it on
 * create (the route coalesces with `description || null`), and unlike omitting it, it is also
 * correct on update.
 *
 * DELIBERATELY INCOMPLETE in one place: the route also reads and writes a `documentation` column
 * (nap-backend migration 0288), and this resource is the only one whose PUT overwrites it
 * unconditionally rather than through the `documentationProvided` CASE WHEN the other twelve use —
 * so omitting it here CLEARS it. That is what the frontend already does, on every save, and adding
 * the key without an editor to fill it would be a second way to say the same thing. The field is
 * owned by #1047, which adds it to all thirteen top-level objects at once; until then this type is
 * knowingly narrower than the wire.
 */
export interface SavePythonLibrary {
  readonly name: string;
  readonly filename: string;
  readonly description: string | null;
  readonly content: string;
  readonly folder_id: number | null;
}

/**
 * `POST /python_libraries` → 201 with the `INSERT … RETURNING` row plus `folder_path` from the
 * shared serializer.
 *
 * Its own type + schema even though the columns coincide with `PythonLibraryRow`: it comes from a
 * different backend statement — equal-by-coincidence, not identity (see ../../README.md).
 * `content` is not returned (the RETURNING clause omits it), so a create response cannot be fed
 * back into an editor that needs the source; the caller refetches instead. `documentation` IS
 * returned and is deliberately not carried — see `SavePythonLibrary`.
 */
export interface CreatedPythonLibrary {
  readonly python_library_id: number;
  readonly name: string;
  readonly filename: string;
  readonly description: string | null;
  readonly hash: string;
  readonly folder_id: number | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly folder_path: FolderPath | null;
}

const createdSchema = object<CreatedPythonLibrary>({
  python_library_id: num(REJECT),
  name: str(),
  filename: str(),
  description: nullable(str()),
  hash: str(),
  folder_id: nullable(num()),
  created_at: nullable(str()),
  updated_at: nullable(str()),
  folder_path: nullable(folderPathSchema),
});

/**
 * POST `/python_libraries`. Returns the created record.
 * Rejects on HTTP error — the library was not created, and the server's complaint (the 400 the
 * route raises for a missing field or a non-`.py` filename) is the useful thing to report.
 * Answers with the **unreadable** arm when the library WAS created and its receipt cannot be
 * read: the row is committed by then, so that is not a failed save and must not be reported as
 * one — a retry would insert a second library (#1035).
 */
export const postPythonLibrary = async (save: SavePythonLibrary): Promise<Received<CreatedPythonLibrary>> => {
  const res = await api.post('/python_libraries', save);
  return readResponse(res.data, { schema: createdSchema, source: 'postPythonLibrary' });
};
